import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useTheme } from "../context/ThemeContext";
import { formatApiError } from "../lib/api";
import { supabase } from "../lib/supabase";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Bell } from "lucide-react";

const DEFAULT_MEAL_REMINDERS = {
  enabled: false,
  breakfast: "08:00",
  lunch: "13:00",
  dinner: "19:00",
};
const DEFAULT_MEDICATION_NOTIFICATIONS = {
  enabled: false,
};

export default function Settings() {
  const { user, updateUser } = useAuth();
  const { t, lang, setLang } = useLang();
  const { theme, setTheme } = useTheme();
  const guestMode =
    typeof window !== "undefined" &&
    localStorage.getItem("guestMode") === "true";
  const [fullName, setFullName] = useState(user?.full_name || user?.name || "");
  const [weight, setWeight] = useState(user?.weight_kg || 70);
  const [reminders, setReminders] = useState(DEFAULT_MEAL_REMINDERS);
  const [medicationNotifications, setMedicationNotifications] = useState(DEFAULT_MEDICATION_NOTIFICATIONS);
  const [saving, setSaving] = useState(false);
  const reminderOwnerId = guestMode ? "guest" : user?.id;
  const mealRemindersKey = reminderOwnerId ? `ddp-meal-reminders-${reminderOwnerId}` : null;
  const medicationNotificationsKey = reminderOwnerId ? `ddp-medication-notifications-${reminderOwnerId}` : null;

  useEffect(() => {
    if (guestMode) {
      try {
        const savedProfile = localStorage.getItem("guestProfile");
        const guestProfile = savedProfile ? JSON.parse(savedProfile) : null;

        if (guestProfile) {
          setFullName(guestProfile.full_name || "");
          setWeight(guestProfile.weight_kg || 70);
        }
      } catch {
        localStorage.removeItem("guestProfile");
      }

      return;
    }

    if (!user?.id) return;

    let isMounted = true;

    const loadProfile = async () => {
      try {
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("full_name, weight_kg")
          .eq("id", user.id)
          .maybeSingle();

        if (error) throw error;
        if (!isMounted) return;

        if (!profile || !profile.full_name) {
          const fallbackName =
            user?.user_metadata?.full_name ||
            user?.user_metadata?.name ||
            "";

          if (fallbackName) {
            setFullName(fallbackName);

            await supabase.from("profiles").upsert({
              id: user.id,
              full_name: fallbackName,
              weight_kg: profile?.weight_kg || 70,
              language: lang,
              dark_mode: theme === "dark",
            }, { onConflict: "id" });
          }

          if (profile?.weight_kg) {
            setWeight(profile.weight_kg);
          }

          return;
        }

        setFullName(profile.full_name || "");
        setWeight(profile.weight_kg || 70);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [guestMode, lang, theme, user?.id, user?.user_metadata?.full_name, user?.user_metadata?.name]);

  useEffect(() => {
    if (!mealRemindersKey) {
      setReminders(DEFAULT_MEAL_REMINDERS);
      return;
    }

    const saved = localStorage.getItem(mealRemindersKey);
    if (!saved) {
      setReminders(DEFAULT_MEAL_REMINDERS);
      return;
    }

    try {
      setReminders({ ...DEFAULT_MEAL_REMINDERS, ...JSON.parse(saved) });
    } catch {
      localStorage.removeItem(mealRemindersKey);
      setReminders(DEFAULT_MEAL_REMINDERS);
    }
  }, [mealRemindersKey]);

  useEffect(() => {
    if (!medicationNotificationsKey) {
      setMedicationNotifications(DEFAULT_MEDICATION_NOTIFICATIONS);
      return;
    }

    const saved = localStorage.getItem(medicationNotificationsKey);
    if (!saved) {
      setMedicationNotifications(DEFAULT_MEDICATION_NOTIFICATIONS);
      return;
    }

    try {
      setMedicationNotifications({ ...DEFAULT_MEDICATION_NOTIFICATIONS, ...JSON.parse(saved) });
    } catch {
      localStorage.removeItem(medicationNotificationsKey);
      setMedicationNotifications(DEFAULT_MEDICATION_NOTIFICATIONS);
    }
  }, [medicationNotificationsKey]);

  const save = async () => {
    const trimmedFullName = fullName.trim();
    const numericWeight = Number(weight);

    if (!trimmedFullName) {
      toast.error("Full name is required.");
      return;
    }

    if (!Number.isFinite(numericWeight) || numericWeight < 30 || numericWeight > 250) {
      toast.error("Enter a valid weight.");
      return;
    }

    setSaving(true);
    try {
      if (guestMode) {
        const guestProfile = {
          full_name: trimmedFullName,
          weight_kg: numericWeight,
          guest: true,
        };

        localStorage.setItem("guestProfile", JSON.stringify(guestProfile));
        if (mealRemindersKey) {
          localStorage.setItem(mealRemindersKey, JSON.stringify(reminders));
        }
        if (medicationNotificationsKey) {
          localStorage.setItem(medicationNotificationsKey, JSON.stringify(medicationNotifications));
        }
        if ((reminders.enabled || medicationNotifications.enabled) && typeof Notification !== "undefined" && Notification.permission === "default") {
          try { await Notification.requestPermission(); } catch {}
        }
        window.dispatchEvent(new Event("ddp-reminders-updated"));
        toast.success(t("saved"));
        return;
      }

      const payload = {
        id: user.id,
        full_name: trimmedFullName,
        weight_kg: numericWeight,
        language: lang,
        dark_mode: theme === "dark",
      };
      const { data, error } = await supabase
        .from("profiles")
        .upsert(payload, { onConflict: "id" })
        .select("full_name, weight_kg")
        .single();
      if (error) throw error;
      if (mealRemindersKey) {
        localStorage.setItem(mealRemindersKey, JSON.stringify(reminders));
      }
      if (medicationNotificationsKey) {
        localStorage.setItem(medicationNotificationsKey, JSON.stringify(medicationNotifications));
      }
      updateUser?.({
        full_name: data.full_name,
        weight_kg: data.weight_kg,
        reminders,
      });
      if ((reminders.enabled || medicationNotifications.enabled) && typeof Notification !== "undefined" && Notification.permission === "default") {
        try { await Notification.requestPermission(); } catch {}
      }
      window.dispatchEvent(new Event("ddp-reminders-updated"));
      toast.success(t("saved"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const notifPerm = typeof Notification !== "undefined" ? Notification.permission : "unsupported";

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>{t("nav_settings")}</h1>
        <p className="text-muted-foreground mt-1">{user?.email}</p>
      </div>

      <div className="bento-card space-y-5">
        <h3 className="font-semibold text-lg">{t("profile")}</h3>
        <div>
          <Label htmlFor="s-name">{t("name")}</Label>
          <Input id="s-name" value={fullName} onChange={(e) => setFullName(e.target.value)} className="h-12 mt-1" data-testid="settings-name" />
        </div>
        <div>
          <Label htmlFor="s-weight">{t("weight_kg")}</Label>
          <Input id="s-weight" type="number" min="30" max="250" value={weight} onChange={(e) => setWeight(e.target.value)} className="h-12 mt-1" data-testid="settings-weight" />
          <p className="text-xs text-muted-foreground mt-1">{t("protein_limit_hint")}</p>
        </div>
      </div>

      <div className="bento-card space-y-5">
        <h3 className="font-semibold text-lg">{t("preferences")}</h3>
        <div>
          <Label>{t("language")}</Label>
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="h-12 mt-1" data-testid="settings-lang"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="en">{t("english")}</SelectItem>
              <SelectItem value="ar">{t("arabic")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between">
          <Label>{t("dark_mode")}</Label>
          <Switch checked={theme === "dark"} onCheckedChange={(v) => setTheme(v ? "dark" : "light")} data-testid="settings-dark" />
        </div>
      </div>

      <div className="bento-card space-y-5">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5" style={{ color: "var(--rs-primary)" }} />
          <h3 className="font-semibold text-lg">{t("food_reminders")}</h3>
        </div>
        <p className="text-sm text-muted-foreground -mt-3">{t("meal_reminders_desc")}</p>

        <div className="flex items-center justify-between">
          <Label>{t("enable_reminders")}</Label>
          <Switch
            checked={reminders.enabled}
            onCheckedChange={(v) => setReminders((r) => ({ ...r, enabled: v }))}
            data-testid="reminders-enabled"
          />
        </div>

        {(reminders.enabled || medicationNotifications.enabled) && notifPerm === "denied" && (
          <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
            {t("notifications_blocked")}
          </div>
        )}

        {reminders.enabled && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>{t("breakfast")}</Label>
                <Input type="time" value={reminders.breakfast} onChange={(e) => setReminders((r) => ({ ...r, breakfast: e.target.value }))} className="h-12 mt-1" data-testid="reminder-breakfast" />
              </div>
              <div>
                <Label>{t("lunch")}</Label>
                <Input type="time" value={reminders.lunch} onChange={(e) => setReminders((r) => ({ ...r, lunch: e.target.value }))} className="h-12 mt-1" data-testid="reminder-lunch" />
              </div>
              <div>
                <Label>{t("dinner")}</Label>
                <Input type="time" value={reminders.dinner} onChange={(e) => setReminders((r) => ({ ...r, dinner: e.target.value }))} className="h-12 mt-1" data-testid="reminder-dinner" />
              </div>
            </div>
          </>
        )}

        <div className="border-t border-border pt-5 space-y-3">
          <div>
            <h4 className="font-semibold">{t("medication_notifications")}</h4>
            <p className="text-sm text-muted-foreground mt-1">{t("medication_notifications_desc")}</p>
          </div>
          <div className="flex items-center justify-between">
            <Label>{t("enable_medication_notifications")}</Label>
            <Switch
              checked={medicationNotifications.enabled}
              onCheckedChange={(enabled) => setMedicationNotifications({ enabled })}
              data-testid="medication-notifications-enabled"
            />
          </div>
        </div>
      </div>

      <Button className="btn-primary h-12 px-8" onClick={save} disabled={saving} data-testid="settings-save">
        {saving ? "…" : t("save")}
      </Button>
    </div>
  );
}
